import type { DiagramDocument } from './types.js';

/** Stored history clones. 20 full copies with photos overflow localStorage (~5 MiB). */
export const MAX_DIAGRAM_VERSIONS = 3;
export const AUTOSAVE_DEBOUNCE_MS = 1000;

export interface DiagramVersionRecord {
  id: string;
  savedAt: string;
  document: DiagramDocument;
}

export function stripPhotosFromDocument<T>(document: T): T {
  if (!document || typeof document !== 'object') return document;
  const clone = structuredClone(document) as { nodes?: Array<{ inventory?: { photoDataUrl?: string } }> };
  if (Array.isArray(clone.nodes)) {
    for (const node of clone.nodes) {
      if (node?.inventory?.photoDataUrl) delete node.inventory.photoDataUrl;
    }
  }
  return clone as T;
}

export function compactDiagramVersions(
  versions: DiagramVersionRecord[],
  opts?: { limit?: number; stripPhotos?: boolean },
): DiagramVersionRecord[] {
  const limit = opts?.limit ?? MAX_DIAGRAM_VERSIONS;
  const sliced = versions.slice(0, Math.max(0, limit));
  if (opts?.stripPhotos === false) return sliced;
  return sliced.map((item) => ({
    ...item,
    document: stripPhotosFromDocument(item.document),
  }));
}

export function pushDiagramVersion(
  versions: DiagramVersionRecord[],
  document: DiagramDocument,
  idFactory: () => string = () => crypto.randomUUID(),
): DiagramVersionRecord[] {
  const next: DiagramVersionRecord = {
    id: idFactory(),
    savedAt: new Date().toISOString(),
    document: stripPhotosFromDocument(document),
  };
  return compactDiagramVersions([next, ...versions]);
}
